import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { formatMoney, type NumberInputValue, toMinorUnits } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

const ADJUSTMENTS_PATH = '/admin/v1/payroll/adjustments';

export interface PayrollAdjustment {
  id: string;
  payrollPeriodId: string;
  staffId: string;
  staffName?: string | null;
  kind: 'extraPayment' | 'unpaidTimeOff' | 'deduction';
  /** Always positive; `kind` carries the sign. */
  amountMinor: number;
  currency: string;
  days?: number | null;
  note: string;
}

interface StaffMember {
  id: string;
  displayName: string;
}

interface Compensation {
  staffId: string;
  type: 'monthlySalary' | 'hourly';
  amountMinor: number;
  currency: string;
}

/** What each kind does to the envelope, in the words the owner would use. */
const KIND_OPTIONS = [
  { value: 'unpaidTimeOff', label: 'Unpaid time off — pay less' },
  { value: 'extraPayment', label: 'Extra payment — pay more' },
  { value: 'deduction', label: 'Deduction — pay less' },
];

const KIND_SIGN: Record<PayrollAdjustment['kind'], 1 | -1> = {
  extraPayment: 1,
  unpaidTimeOff: -1,
  deduction: -1,
};

/**
 * The manual movements on this period's pay — time off, extra payments, deductions — which are the
 * record of the payroll that ACTUALLY happened. A cook takes four unpaid days; somebody covers his
 * shifts and is handed something for it; an advance is worked back. The generator computes none of
 * that, so before this panel the register's figures and the envelopes could only agree by luck.
 *
 * Adjustments attach to the PERIOD and reach the figures when the register is next built — which is
 * why the panel owns a Rebuild button: entering a correction and reading a register that does not
 * include it is exactly the confusion this screen must not manufacture. Locked once the register is
 * signed off, same as every other input to the figures.
 */
export function AdjustmentsPanel({
  periodId,
  runStatus,
  onRebuilt,
}: {
  periodId: string;
  runStatus: string;
  onRebuilt: () => void;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  /** Set the moment the list changes, cleared by Rebuild: the figures above are behind the record. */
  const [stale, setStale] = useState(false);

  const adjustments = useQuery({
    queryKey: listKey(ADJUSTMENTS_PATH, { periodId }),
    queryFn: () => rawApi<PayrollAdjustment[]>('GET', `${ADJUSTMENTS_PATH}?periodId=${periodId}`),
  });

  const afterChange = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(ADJUSTMENTS_PATH, { periodId }) });
    setStale(true);
  };

  const remove = useMutation({
    mutationFn: (id: string) => rawApi('DELETE', `${ADJUSTMENTS_PATH}/${id}`),
    onSuccess: () => {
      notifyDone('Adjustment removed', 'Rebuild the register to take it back out of the figures.');
      afterChange();
    },
    onError: (err) => notifyError(err, 'remove this adjustment'),
  });

  const rebuild = useMutation({
    mutationFn: () => rawApi('POST', `/admin/v1/payroll/periods/${periodId}/runs`, {}),
    onSuccess: () => {
      setStale(false);
      notifyDone('Register rebuilt', 'The figures above now include every adjustment listed here.');
      onRebuilt();
    },
    onError: (err) => notifyError(err, 'rebuild this register'),
  });

  const rows = adjustments.data ?? [];
  const editable = runStatus === 'draft';

  return (
    <Paper withBorder radius="md" p="lg">
      <Group justify="space-between" mb="xs">
        <Title order={5}>Adjustments</Title>
        {editable && (
          <Group gap="xs">
            <Button size="compact-sm" variant="light" onClick={() => setAdding(true)}>
              Add an adjustment
            </Button>
          </Group>
        )}
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Days off, extra payments, and deductions for these dates — the things the clock and the salary cannot
        know. They reach the figures when the register is rebuilt.
      </Text>

      {stale && editable && (
        <Alert color="yellow" variant="light" mb="md">
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm">The figures above do not include what changed here yet.</Text>
            <Button size="compact-sm" loading={rebuild.isPending} onClick={() => rebuild.mutate()}>
              Rebuild the register
            </Button>
          </Group>
        </Alert>
      )}

      {rows.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          Nothing manual on this period.
        </Text>
      ) : (
        <Table verticalSpacing="xs">
          <Table.Tbody>
            {rows.map((a) => (
              <Table.Tr key={a.id}>
                <Table.Td fw={600}>{a.staffName ?? 'Unknown'}</Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600} c={KIND_SIGN[a.kind] < 0 ? 'red' : 'teal'}>
                    {KIND_SIGN[a.kind] < 0 ? '−' : '+'}
                    {formatMoney(a.amountMinor, a.currency)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {a.note}
                    {a.days ? (
                      <Text span size="xs" c="dimmed">
                        {` · ${a.days} ${a.days === 1 ? 'day' : 'days'}`}
                      </Text>
                    ) : null}
                  </Text>
                </Table.Td>
                <Table.Td w={40}>
                  {editable && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label="Remove this adjustment"
                      onClick={() => remove.mutate(a.id)}
                    >
                      ✕
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {adding && (
        <AddAdjustmentModal
          periodId={periodId}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            afterChange();
          }}
        />
      )}
    </Paper>
  );
}

/**
 * One adjustment, in the order the owner thinks it: who, what happened, how much. For unpaid time
 * off on a salary the amount offers itself — a day is the monthly salary ÷ 30, the working rule for
 * Honduran wages — and stays editable, because the offer is a convenience and the record is what he
 * confirms.
 */
function AddAdjustmentModal({
  periodId,
  onClose,
  onSaved,
}: {
  periodId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [staffId, setStaffId] = useState<string | null>(null);
  const [kind, setKind] = useState<string | null>('unpaidTimeOff');
  const [days, setDays] = useState<NumberInputValue>('');
  const [amount, setAmount] = useState<NumberInputValue>('');
  const [amountTouched, setAmountTouched] = useState(false);
  const [currency, setCurrency] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const members = useQuery({
    queryKey: listKey('/admin/v1/staff/members'),
    queryFn: () => rawApi<StaffMember[]>('GET', '/admin/v1/staff/members'),
  });

  // The pay on file for whoever is chosen — newest first, so [0] is what they are on now. It seeds
  // the currency and prices a day of time off; both stay editable.
  const comps = useQuery({
    queryKey: listKey('/admin/v1/payroll/compensations', { staffId: staffId ?? '' }),
    queryFn: () => rawApi<Compensation[]>('GET', `/admin/v1/payroll/compensations?staffId=${staffId}`),
    enabled: Boolean(staffId),
  });
  const current = comps.data?.[0];

  const dayCount = typeof days === 'number' ? days : Number.parseInt(String(days || '0'), 10) || 0;
  /** Salary ÷ 30 × days, in MAJOR units for the input — offered, never imposed. */
  const suggestedAmount =
    kind === 'unpaidTimeOff' && current?.type === 'monthlySalary' && dayCount > 0
      ? Math.round((current.amountMinor / 30) * dayCount) / 100
      : null;

  const effectiveAmount = amountTouched || suggestedAmount === null ? amount : suggestedAmount;
  const effectiveCurrency = currency ?? current?.currency ?? 'HNL';
  const amountMinor = toMinorUnits(effectiveAmount) ?? 0;

  const save = useMutation({
    mutationFn: () =>
      rawApi('POST', ADJUSTMENTS_PATH, {
        payrollPeriodId: periodId,
        staffId,
        kind,
        amountMinor,
        currency: effectiveCurrency,
        days: kind === 'unpaidTimeOff' && dayCount > 0 ? dayCount : undefined,
        note: note.trim(),
      }),
    onSuccess: () => {
      notifyDone('Adjustment recorded', 'Rebuild the register to bring it into the figures.');
      onSaved();
    },
    onError: (err) => notifyError(err, 'record this adjustment'),
  });

  return (
    <Modal opened onClose={onClose} title="Add an adjustment" size="md">
      <Stack gap="md">
        <Select
          label="Who"
          placeholder="Pick a person"
          searchable
          data={(members.data ?? []).map((m) => ({ value: m.id, label: m.displayName }))}
          value={staffId}
          onChange={(v) => {
            setStaffId(v);
            setCurrency(null);
          }}
          comboboxProps={{ withinPortal: true }}
        />
        <Select
          label="What happened"
          data={KIND_OPTIONS}
          value={kind}
          onChange={setKind}
          comboboxProps={{ withinPortal: true }}
        />
        {kind === 'unpaidTimeOff' && (
          <NumberInput
            label="Days off"
            description={
              current?.type === 'monthlySalary'
                ? 'A day of a monthly salary is the salary ÷ 30 — the amount below is offered from that and can be corrected.'
                : undefined
            }
            min={1}
            max={31}
            value={days}
            onChange={setDays}
          />
        )}
        <Group grow>
          <NumberInput
            label="Amount"
            description={
              KIND_SIGN[(kind ?? 'deduction') as PayrollAdjustment['kind']] < 0
                ? 'Taken off their pay'
                : 'Added to their pay'
            }
            min={0}
            decimalScale={2}
            value={effectiveAmount}
            onChange={(v) => {
              setAmountTouched(true);
              setAmount(v);
            }}
          />
          <Select
            label="Currency"
            data={[
              { value: 'HNL', label: 'Lempiras' },
              { value: 'USD', label: 'Dollars' },
            ]}
            value={effectiveCurrency}
            onChange={setCurrency}
            comboboxProps={{ withinPortal: true }}
          />
        </Group>
        <Textarea
          label="Why"
          description="This is the record — write it so it still makes sense in March."
          placeholder="Took 4 days off · Covered the weekend shifts · Advance from July worked back"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          autosize
          minRows={2}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={!staffId || !kind || amountMinor <= 0 || !note.trim()}
            onClick={() => save.mutate()}
          >
            Record it
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
